import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash, ArrowLeft, Circle, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../store';
import { User as UserType, UserRole } from '../types';
import { supabase } from '../lib/supabase';

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { users, drivers, addUser, updateUser, setUserPassword, currentUser } = useAppStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Filter out current user from the list
  const filteredUsers = users.filter(user => user.id !== currentUser?.id);
  
  // New user form state
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'logistics' as UserRole,
  });
  
  // Edit user form state
  const [editUser, setEditUser] = useState({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    role: 'logistics' as UserRole,
  });

  // Set password form state
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  
  // Initialize edit form
  useEffect(() => {
    if (selectedUserId) {
      const user = users.find(u => u.id === selectedUserId);
      if (user) {
        setEditUser({
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: user.role,
        });
      }
    }
  }, [selectedUserId, users]);
  
  // Handle new user form changes
  const handleNewUserChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle edit user form changes
  const handleEditUserChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditUser(prev => ({ ...prev, [name]: value }));
  };

  // Handle password form changes
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle new user form submission
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    // Check if user has admin role before proceeding
    if (!currentUser || currentUser.role !== 'admin') {
      setError('Vous n\'avez pas les droits nécessaires pour créer des utilisateurs. Seuls les administrateurs peuvent créer de nouveaux utilisateurs.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Call the Supabase Edge Function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const createUserUrl = `${supabaseUrl}/functions/v1/create-user`;
      
      const { data: sessionData } = await supabase.auth.getSession();
      
      const response = await fetch(createUserUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session?.access_token}`
        },
        body: JSON.stringify({
          email: newUser.email,
          password: newUser.password,
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          role: newUser.role,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create user');
      }
      
      // Reload users data
      await useAppStore.getState().loadInitialData();
      
      setNewUser({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'logistics',
      });
      
      setSuccessMessage('Utilisateur créé avec succès');
      setShowAddModal(false);
    } catch (error: any) {
      console.error('Error adding user:', error);
      setError('Une erreur est survenue lors de la création de l\'utilisateur. Veuillez réessayer ou contacter un administrateur.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle edit user form submission
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    if (!currentUser || currentUser.role !== 'admin') {
      setError('Vous n\'avez pas les droits nécessaires pour modifier des utilisateurs. Seuls les administrateurs peuvent modifier les utilisateurs.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      await updateUser(editUser.id, {
        firstName: editUser.firstName,
        lastName: editUser.lastName,
        email: editUser.email,
        role: editUser.role,
      });
      
      setSuccessMessage('Utilisateur mis à jour avec succès');
      setShowEditModal(false);
      setSelectedUserId(null);
    } catch (error: any) {
      console.error('Error updating user:', error);
      const errorMessage = error?.message || error?.error_description || 'Une erreur est survenue lors de la modification de l\'utilisateur.';
      setError(`${errorMessage} Veuillez réessayer ou contacter un administrateur.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle set password form submission
  const handleSetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    if (!currentUser || currentUser.role !== 'admin') {
      setError('Vous n\'avez pas les droits nécessaires pour modifier les mots de passe. Seuls les administrateurs peuvent modifier les mots de passe.');
      return;
    }
    
    if (!selectedUserId) {
      setError('Aucun utilisateur sélectionné');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      await setUserPassword(selectedUserId, passwordData.newPassword);
      
      setSuccessMessage('Mot de passe modifié avec succès');
      setShowSetPasswordModal(false);
      setSelectedUserId(null);
      setPasswordData({
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      console.error('Error setting password:', error);
      setError(error.message || 'Une erreur est survenue lors de la modification du mot de passe');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle user deletion
  const handleDeleteUser = async () => {
    setError(null);
    setSuccessMessage(null);
    
    if (!currentUser || currentUser.role !== 'admin') {
      setError('Vous n\'avez pas les droits nécessaires pour supprimer des utilisateurs. Seuls les administrateurs peuvent supprimer les utilisateurs.');
      return;
    }
    
    if (selectedUserId) {
      setSelectedUserId(null);
      setShowDeleteModal(false);
      setSuccessMessage('Fonctionnalité non implémentée');
    }
  };
  
  // Check if user is admin
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <h2 className="text-xl font-bold mb-2">Accès refusé</h2>
        <p>Vous n'avez pas les droits nécessaires pour accéder à cette page. Seuls les administrateurs peuvent gérer les utilisateurs.</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 px-4 py-2 bg-blue-800 text-white rounded-lg"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }
  
  return (
    <div>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4\" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow mb-4 p-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <h2 className="text-xl font-bold">Gestion des utilisateurs ({filteredUsers.length})</h2>
        </div>
        
        <button
          onClick={() => setShowAddModal(true)}
          className={`bg-blue-800 text-white px-4 py-2 rounded-lg flex items-center ${
            currentUser?.role !== 'admin' ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          disabled={currentUser?.role !== 'admin'}
          title={currentUser?.role !== 'admin' ? 'Seuls les administrateurs peuvent créer des utilisateurs' : undefined}
        >
          <Plus className="h-4 w-4 mr-2" />
          Ajouter un utilisateur
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nom
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rôle
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className={`rounded-full h-8 w-8 flex items-center justify-center mr-3 ${
                      user.role === 'admin' ? 'bg-red-100 text-red-800' :
                      user.role === 'logistics' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {user.firstName[0]}{user.lastName[0]}
                    </div>
                    <div>
                      <div className="font-medium">{user.firstName} {user.lastName}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.role === 'admin' ? 'bg-red-100 text-red-800' :
                    user.role === 'logistics' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {user.role === 'admin' ? 'Administrateur' :
                     user.role === 'logistics' ? 'Logistique' : 'Chauffeur'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Circle className={`h-3 w-3 mr-2 ${user.isOnline ? 'text-green-500 fill-green-500' : 'text-gray-400 fill-gray-400'}`} />
                    <span className="text-sm">
                      {user.isOnline ? 'En ligne' : 'Hors ligne'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setError(null);
                        setSuccessMessage(null);
                        setSelectedUserId(user.id);
                        setShowEditModal(true);
                      }}
                      className="text-blue-800 hover:text-blue-600"
                      title="Modifier"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    
                    <button
                      onClick={() => {
                        setError(null);
                        setSuccessMessage(null);
                        setSelectedUserId(user.id);
                        setShowDeleteModal(true);
                      }}
                      className="text-red-800 hover:text-red-600"
                      title="Supprimer"
                    >
                      <Trash className="h-5 w-5" />
                    </button>

                    <button
                      onClick={() => {
                        setError(null);
                        setSuccessMessage(null);
                        setSelectedUserId(user.id);
                        setPasswordData({
                          newPassword: '',
                          confirmPassword: '',
                        });
                        setShowSetPasswordModal(true);
                      }}
                      className="text-purple-800 hover:text-purple-600"
                      title="Définir le mot de passe"
                    >
                      <Key className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Ajouter un utilisateur</h2>
            
            <form onSubmit={handleAddSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="firstName" className="block text-gray-700 font-medium mb-2">
                    Prénom
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={newUser.firstName}
                    onChange={handleNewUserChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-gray-700 font-medium mb-2">
                    Nom
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={newUser.lastName}
                    onChange={handleNewUserChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={newUser.email}
                  onChange={handleNewUserChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="password" className="block text-gray-700 font-medium mb-2">
                  Mot de passe
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={newUser.password}
                  onChange={handleNewUserChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="role" className="block text-gray-700 font-medium mb-2">
                  Rôle
                </label>
                <select
                  id="role"
                  name="role"
                  value={newUser.role}
                  onChange={handleNewUserChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="logistics">Logistique</option>
                  <option value="driver">Chauffeur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setSuccessMessage(null);
                    setShowAddModal(false);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-800 text-white rounded-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Création...' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit User Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Modifier l'utilisateur</h2>
            
            <form onSubmit={handleEditSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="editFirstName" className="block text-gray-700 font-medium mb-2">
                    Prénom
                  </label>
                  <input
                    type="text"
                    id="editFirstName"
                    name="firstName"
                    value={editUser.firstName}
                    onChange={handleEditUserChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="editLastName" className="block text-gray-700 font-medium mb-2">
                    Nom
                  </label>
                  <input
                    type="text"
                    id="editLastName"
                    name="lastName"
                    value={editUser.lastName}
                    onChange={handleEditUserChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label htmlFor="editEmail" className="block text-gray-700 font-medium mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="editEmail"
                  name="email"
                  value={editUser.email}
                  onChange={handleEditUserChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="editRole" className="block text-gray-700 font-medium mb-2">
                  Rôle
                </label>
                <select
                  id="editRole"
                  name="role"
                  value={editUser.role}
                  onChange={handleEditUserChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="logistics">Logistique</option>
                  <option value="driver">Chauffeur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setSuccessMessage(null);
                    setShowEditModal(false);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-800 text-white rounded-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete User Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Supprimer l'utilisateur</h2>
            
            <p className="mb-4">
              Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.
            </p>
            
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setError(null);
                  setSuccessMessage(null);
                  setShowDeleteModal(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                Annuler
              </button>
              
              <button
                onClick={handleDeleteUser}
                className="px-4 py-2 bg-red-600 text-white rounded-lg"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set Password Modal */}
      {showSetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Définir un nouveau mot de passe</h2>
            
            <form onSubmit={handleSetPasswordSubmit}>
              <div className="mb-4">
                <label htmlFor="newPassword" className="block text-gray-700 font-medium mb-2">
                  Nouveau mot de passe
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  minLength={6}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Le mot de passe  doit contenir au moins 6 caractères.
                </p>
              </div>
              
              <div className="mb-4">
                <label htmlFor="confirmPassword" className="block text-gray-700 font-medium mb-2">
                  Confirmer le mot de passe
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setSuccessMessage(null);
                    setShowSetPasswordModal(false);
                    setSelectedUserId(null);
                    setPasswordData({
                      newPassword: '',
                      confirmPassword: '',
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enregistrement...' : 'Définir le mot de passe'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;